import {AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, Optional, ViewChild, ViewEncapsulation} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Event, EventInfoSmall, EventLocationType, Manager, RepresentativeBasic} from '../../generated/sales-terminal-client';
import {cloneDeep, find, isEqual, orderBy, throttle} from 'lodash-es';
import {AuthService} from '../../services/auth/auth.service';
import {SnackService} from '../../services/snack/snack.service';
import {TranslateService} from '@ngx-translate/core';
import {CONTINUE_WITHOUT_SAVING, TO_EVENT_ACTION, TO_EVENT_BO_VIEW} from '../../modules/sales-terminal/sales-terminal.constants';
import {MatTabGroup} from '@angular/material/tabs';
import {UtilService} from '../../services/util/util.service';
import {ViewService} from '../../services/view/view.service';
import {concat, Observable, Subscription} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {Privileges} from '../../modules/admin/privileges/privileges.constants';
import {PendingChangesDialogComponent} from '../pending-changes-dialog/pending-changes-dialog.component';
import {SelfServiceEventService} from "../../services/self-service/event/self-service-event.service";
import {LegalPersonService} from "../../services/legalPerson/legalPerson.service";
import {HeightMultiplierEnum} from '../html-more/html-more.component';
import {EventService} from '../../services/event/event.service';
import {S3UploaderRatios, S3UploaderTypes} from '../s3/uploader/s3-uploader.types';
import {EventProps} from '../event-info-props/event-info-props.component';
import {CartService} from '../../services/cart/cart.service';

export interface EventDetailsDialogData {
  event: Event;
  displayNavigateToEvent: boolean;
}

@Component({
  selector: 'app-event-details-dialog',
  templateUrl: './event-details-dialog.component.html',
  styleUrls: ['./event-details-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EventDetailsDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  public event: Event = {};
  public activeTab = 0;
  isSeason = false;
  isPartOfSeason = false;
  eventManagers: Manager[] = [];
  public updatedManagers: string[] = [];
  updatedManagersOrig: string[] = [];
  availableManagers: RepresentativeBasic[] = [];
  isEdit = false;
  tabsMap: HTMLElement[] = [];
  hotInfoTabIndex: number;
  seasonTabIndex: number;
  descriptionTabIndex: number;
  managersTabIndex: number;
  displayNavigateToEvent: boolean;
  scrollTabChange = false;
  clickTabChange = false;
  swipeCoord: [number, number];
  swipeTime: number;
  swipeMax: number;
  public eventProps: EventProps;
  public isOwnerCentre: boolean;
  public HeightMultiplierEnum = HeightMultiplierEnum;
  private _subs: Subscription[] = [];

  @ViewChild('priceInfo') priceInfoElement: ElementRef<HTMLElement>;
  @ViewChild('hotInfo') hotInfoElement: ElementRef<HTMLElement>;
  @ViewChild('description') descriptionElement: ElementRef<HTMLElement>;
  @ViewChild('season') seasonElement: ElementRef<HTMLElement>;
  @ViewChild('managers') managersElement: ElementRef<HTMLElement>;
  @ViewChild('managersEdit') managersEditElement: ElementRef<HTMLElement>;
  @ViewChild('tabsContent') tabsContentElement: ElementRef<HTMLElement>;
  @ViewChild('tabGroup') tabGroupElement: MatTabGroup;

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) public data: EventDetailsDialogData,
              public dialogRef: MatDialogRef<EventDetailsDialogComponent>,
              public utilService: UtilService,
              public authService: AuthService,
              public legalService: LegalPersonService,
              public viewService: ViewService,
              public eventService: EventService,
              public cartService: CartService,
              private selfServiceEventService: SelfServiceEventService,
              private snackService: SnackService,
              private translateService: TranslateService,
              private dialog: MatDialog) {
    this.event = data.event;
    this.displayNavigateToEvent = data.displayNavigateToEvent;
    this.isSeason = this.event.eventId === this.event.seasonId;
    this.isPartOfSeason = this.event.seasonId && this.event.eventId !== this.event.seasonId;
    this.swipeMax = this.isSeason ? 3 : 2;
    this.eventProps = {
      eventPeriod: this.event.event,
      timezone: this.event.timezone,
      venueTitle: this.event.venueTitle,
      onlineLocation: this.event.onlineLocation
    }
  }

  get seasonEvents(): EventInfoSmall[] {
    return this.event?.seasonEvents && orderBy(this.event.seasonEvents, 'eventDate.start', 'asc') || [];
  }

  get image(): string {
    return this.eventService.getEventImage(this.event);
  }

  public ngOnInit(): void {
    this.dialogRef.disableClose = true;
    this.isOwnerCentre = this.event.externalOwnerCentreId === this.authService.user.centreId;
    this._subs.push(this.dialogRef.backdropClick().subscribe(() => {
      this.confirmClose();
    }));
    this.getManagers();
  }
  public ngAfterViewInit(): void {
    this.buildTabs();
    this.tabsContentElement.nativeElement.addEventListener('scroll', throttle(this.onScroll.bind(this), 100));
  }

  public ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
    this._subs = [];
  }

  syncModels(): void {
    this.updatedManagersOrig = cloneDeep(this.updatedManagers);
  }

  get isModelChanged(): boolean {
    return !isEqual(this.updatedManagers, this.updatedManagersOrig);
  }

  buildTabs(): void {
    let tabIndex = 0;
    this.tabsMap.push(this.priceInfoElement.nativeElement);
    if (!this.event.boEvent) {
      this.hotInfoTabIndex = tabIndex++;
      this.tabsMap.push(this.hotInfoElement.nativeElement);
    }
    this.descriptionTabIndex = tabIndex++;
    this.tabsMap.push(this.descriptionElement.nativeElement);
    if (this.isSeason) {
      this.seasonTabIndex = tabIndex++;
      this.tabsMap.push(this.seasonElement.nativeElement);
    }
    if (!this.event.boEvent && this.canEditEventManagers) {
      this.managersTabIndex = tabIndex;
      this.tabsMap.push(this.managersElement.nativeElement);
    }
  }

  public getManagers(): void {
    if (!this.event.boEvent && this.authService.checkPrivileges([Privileges.EVENT_MANAGERS_MANAGE, Privileges.SELF_SERVICE_EVENTS_MANAGE])) {
      this.selfServiceEventService.getEventManagers(this.event.eventId).subscribe((results: any) => {
        this.eventManagers = results.managers || [];
        this.updatedManagers = this.eventManagers.map(m => m.uuid);
        this.syncModels();
      });
      this.legalService.findLegalRepresentatives(this.event.externalOwnerCentreId).subscribe((results: RepresentativeBasic[]) =>
        this.availableManagers = results);
    }
  }

  changeTab(target: number): void {
    if (target != null) {
      if (this.scrollTabChange) {
        this.scrollTabChange = false;
        return;
      }
      this.clickTabChange = true;
      this.tabsMap[target].scrollIntoView();
    }
  }

  public save(): void {
    const observables: Observable<any>[] = [];
    const toRemove = this.eventManagers.filter(m => !this.updatedManagers.includes(m.uuid)).map(m => m.uuid);
    const toAdd = this.updatedManagers.filter(uuid => !find(this.eventManagers, { uuid: uuid }));

    if (toRemove.length) {
      observables.push(this.selfServiceEventService.removeEventManagers(this.event.eventId, toRemove));
    }
    if (toAdd.length) {
      observables.push(this.selfServiceEventService.addEventManagers(this.event.eventId, toAdd));
    }

    if (observables.length) {
      concat(...observables).pipe(
        finalize(() => this.isEdit = false)
      ).subscribe({
          complete: () => {
            this.getManagers();
            this.snackService.showSuccess(this.translateService.instant('general.api_event_managers_successfully_saved'));
          }
        });
    } else {
      this.snackService.showSuccess(this.translateService.instant('general.api_event_managers_successfully_saved'));
      this.isEdit = false;
    }
  }

  get canEditEventManagers(): boolean {
    return this.authService.checkPrivileges([Privileges.EVENT_MANAGERS_MANAGE, Privileges.SELF_SERVICE_EVENTS_MANAGE]);
  }

  public navigateToEvent(): void {
    this.dialogRef.close(TO_EVENT_ACTION);
  }
  public navigateToSelfServiceView(): void {
    this.dialogRef.close(TO_EVENT_BO_VIEW);
  }

  editManagers(): void {
    this.isEdit = true;
    setTimeout(() => {
      this.managersEditElement.nativeElement.scrollIntoView();
    }, 0);
  }

  onScroll(): void {
    if (this.clickTabChange) {
      this.clickTabChange = false;
      return;
    }

    // Add all available element top/bottom positions
    const tabsContentBottom = this.tabsContentElement ? this.tabsContentElement.nativeElement.getBoundingClientRect().bottom : null;
    const elementTops = [];
    if (this.managersElement) elementTops.push(this.managersElement.nativeElement.getBoundingClientRect().top);
    if (this.seasonElement) elementTops.push(this.seasonElement.nativeElement.getBoundingClientRect().top);
    if (this.descriptionElement) elementTops.push(this.descriptionElement.nativeElement.getBoundingClientRect().top);
    if (this.hotInfoElement) elementTops.push(this.hotInfoElement.nativeElement.getBoundingClientRect().top);

    // Get first visible element top index and convert to tab index value
    const topIndex = elementTops.findIndex(top => top <= tabsContentBottom);
    const newTabIndex = topIndex !== -1 ? elementTops.length - topIndex : 0;

    if (this.tabGroupElement.selectedIndex !== newTabIndex) {
      this.scrollTabChange = true;
      this.tabGroupElement.selectedIndex = newTabIndex;
    }
  }

  swipe(event: TouchEvent, timing: string): void {
    const coordinates: [number, number] = [event.changedTouches[0].clientX, event.changedTouches[0].clientY];
    const time = new Date().getTime();
    if (timing === 'start') {
      this.swipeCoord = coordinates;
      this.swipeTime = time;
    } else if (timing === 'end') {
      const direction = [coordinates[0] - this.swipeCoord[0], coordinates[1] - this.swipeCoord[1]];
      const duration = time - this.swipeTime;
      if (duration < 1000 && Math.abs(direction[0]) > 30 && Math.abs(direction[0]) > Math.abs(direction[1] * 3)) {
        const swipe = direction[0] < 0 ? 'next' : 'previous';
        if (swipe === 'next'){
          const isFirst = this.activeTab === 0;
          if (this.activeTab < this.swipeMax){
            this.activeTab = isFirst ? 1 : this.activeTab + 1;
          }
        } else if (swipe === 'previous'){
          if (this.activeTab >= 1){
            this.activeTab = this.activeTab - 1;
          }
        }
      }
    }
  }

  public confirmClose(): void {
    if (!this.isModelChanged) {
      this.close();
    } else {
      const pendingChanges = new PendingChangesDialogComponent(this.dialog, this.translateService, {
        pendingChangesDescription: this.translateService.instant('general.api_event_managers_description_unsaved_changes')
      });
      pendingChanges.dialogRef.afterClosed().subscribe((res: any) => {
        if (res === true) {
          this.save();
        } else if (res === CONTINUE_WITHOUT_SAVING) {
          this.close();
        }
      });
    }
  }

  private close(): void {
    this.dialogRef.disableClose = false;
    this.dialogRef.close();
  }

  public get canViewSelfServiceEvent(): boolean {
    return this.event.boEvent && this.authService.hasSelfServiceEventsAccessPrivilege &&
      (this.isOwnerCentre || this.authService.user.isSuperCentre);
  }

  protected readonly EventLocationType = EventLocationType;
  protected readonly Privileges = Privileges;
  protected readonly S3UploaderTypes = S3UploaderTypes;
  protected readonly S3UploaderRatios = S3UploaderRatios;
}
